import { Outlet, useLocation, Link } from 'react-router-dom';
import {
  AppShell,
  Burger,
  Group,
  NavLink,
  ScrollArea,
  Text,
  Avatar,
  Button,
  Stack,
  Divider,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconHome,
  IconUsers,
  IconSettings,
  IconBookmark,
  IconChartBar,
  IconQuestionMark,
  IconIdBadge,
  IconAlertTriangle,
  IconClipboardList,
  IconListDetails,
  IconChecklist,
  IconLogout,
  IconServer,
  IconHistory,
  IconMapPin,
} from '@tabler/icons-react';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { canAccess } from '@/lib/utils/roles';
import type { UserRole } from '@/types/entities';
import favicon from '@/assets/images/favicon.png';

interface NavItem {
  label: string;
  icon: React.ReactNode;
  to: string;
  role: UserRole;
  roles?: UserRole[];
}

const navItems: NavItem[] = [
  { label: 'Assessment Dashboard', icon: <IconHome size={18} />, to: '/dashboard', role: 'BIOMETRIC' },
  { label: 'Assessment Management', icon: <IconListDetails size={18} />, to: '/dashboard/exam-settings', role: 'EXAM_OFFICER' },
  { label: 'Exam Operations', icon: <IconChartBar size={18} />, to: '/dashboard/exam-operations', role: 'EXAM_OFFICER', roles: ['EXAM_OFFICER', 'TECHNICAL_SUPPORT_OFFICER'] },
  { label: 'Venues', icon: <IconMapPin size={18} />, to: '/dashboard/venues', role: 'LOCATION_MANAGER' },
  { label: 'Exam Results', icon: <IconBookmark size={18} />, to: '/dashboard/result', role: 'ADMIN' },
  { label: 'Quiz Statistics', icon: <IconChartBar size={18} />, to: '/dashboard/quiz-stats', role: 'ADMIN' },
  { label: 'Question Review', icon: <IconChecklist size={18} />, to: '/dashboard/question-review', role: 'ADMIN' },
  { label: 'External Exams', icon: <IconClipboardList size={18} />, to: '/dashboard/external-exams', role: 'ADMIN' },
  { label: 'Questions', icon: <IconQuestionMark size={18} />, to: '/dashboard/question', role: 'QUESTION_MANAGEMENT' },
  { label: 'Users', icon: <IconUsers size={18} />, to: '/dashboard/users', role: 'ADMIN' },
  { label: 'Registrations', icon: <IconIdBadge size={18} />, to: '/dashboard/registrations', role: 'BIOMETRIC' },
  { label: 'Malpractice', icon: <IconAlertTriangle size={18} />, to: '/dashboard/malpractice', role: 'ADMIN' },
  { label: 'Configuration', icon: <IconSettings size={18} />, to: '/dashboard/settings', role: 'ADMIN' },
  { label: 'App Management', icon: <IconServer size={18} />, to: '/dashboard/app-management', role: 'ADMIN' },
  { label: 'Audit Logs', icon: <IconHistory size={18} />, to: '/dashboard/audit-logs', role: 'ADMIN' },
];

export function AdminLayout() {
  const [opened, { toggle }] = useDisclosure();
  const location = useLocation();
  const { user, getUserRoles } = useAuthStore();
  const { logout, isLoading } = useAuth();

  const userRoles = getUserRoles();

  const filteredNavItems = navItems.filter((item) =>
    (item.roles ?? [item.role]).some(role => canAccess(userRoles, role))
  );

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{ width: 280, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Avatar src={favicon} size={32} />
            <Text size="lg" fw={700} c="primary">
              CBT Admin
            </Text>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        <AppShell.Section grow component={ScrollArea}>
          {filteredNavItems.map((item) => (
            <NavLink
              key={item.to}
              component={Link}
              to={item.to}
              label={item.label}
              leftSection={item.icon}
              active={location.pathname === item.to}
              style={{ borderRadius: 'var(--mantine-radius-md)', marginBottom: 4 }}
            />
          ))}
        </AppShell.Section>

        <AppShell.Section>
          <Divider mb="sm" />
          <Stack gap="xs">
            <Group gap="sm" wrap="nowrap">
              <Avatar radius="xl" size="md" />
              <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
                <Text size="sm" fw={500} truncate>
                  {user?.role?.toString()}
                </Text>
                <Text size="xs" c="dimmed" truncate>
                  {user?.username}
                </Text>
              </Stack>
            </Group>
            <Button
              variant="light"
              color="red"
              fullWidth
              leftSection={<IconLogout size={16} />}
              onClick={logout}
              loading={isLoading}
              radius="md"
            >
              Log out
            </Button>
          </Stack>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
